import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fmtCurrency, POPULAR_PAY_TRADES } from '../utils/tradePayData';
import { loadPublishedWageRatesOnce, unionScaleForPayTrade, PAY_TRADE_UNION_SCALE } from '../utils/unionWages';
import useIsPhone from '../utils/useIsPhone';

const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";
const HELPER    = 'rgba(255,255,255,0.72)';

// Small line icons (24px grid, drawn with currentColor) -- one per popular trade.
const ICON_PATHS = {
  'Electrician':              'M13 2 4 14h7l-1 8 9-12h-7l1-8z',
  'Plumber':                  'M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z',
  'Carpenter':                'M14 4l6 6-3 3-6-6 3-3zM11 7 3 15l3 3 8-8M14 4h-4',
  'HVAC Technician':          'M12 2v20M4.9 7l14.2 10M4.9 17 19.1 7M9 4l3 2 3-2M9 20l3-2 3 2',
  'Welder and Fabricator':    'M12 22c-4 0-7-3-7-7 0-5 5-7 5-13 3 2 4 5 4 7 1-1 2-2 2-4 2 2 3 5 3 8 0 5-3 9-7 9z',
  'Cement Mason':             'M3 21l2.5-7.5L13 6l5 5-7.5 7.5L3 21zM16 8l4-4',
  'Construction Laborer':     'M2 18h20M4 18v-2a8 8 0 0 1 16 0v2M10 8.5V13M14 8.5V13',
  'Heavy Equipment Operator': 'M4 16h8a2 2 0 0 1 0 4H4a2 2 0 0 1 0-4zM4 16v-5h5l2 5M9 11l5-6 5 4M19 9l2 5h-4l2-5',
};

function TradeIcon({ trade }) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
      <path d={ICON_PATHS[trade]} />
    </svg>
  );
}

// "Popular trades" grid at the top of the Career Pay Guide: one tile per popular trade that
// has a row for the selected state (rowsForState), showing its yearly median. A tile gets the
// "Union scale available" badge only when the trade is in PAY_TRADE_UNION_SCALE *and* the
// published wage rows actually give it a union scale -- the same check UnionScaleBlock uses
// before it renders, so the badge never promises a block that won't appear.
export default function PopularTradeTiles({ rowsForState, selectedTrade, onSelect }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const [unionTrades, setUnionTrades] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    loadPublishedWageRatesOnce().then(res => {
      if (cancelled || res.error) return;
      setUnionTrades(new Set(POPULAR_PAY_TRADES.filter(name => PAY_TRADE_UNION_SCALE[name] && unionScaleForPayTrade(res.rows, name))));
    });
    return () => { cancelled = true; };
  }, []);

  const tiles = POPULAR_PAY_TRADES.map(name => rowsForState.find(r => r.trade === name)).filter(Boolean);
  if (tiles.length === 0) return null;

  return (
    <section data-popular-trades aria-labelledby="popular-trades-heading" style={{ marginBottom: '16px' }}>
      <h2 id="popular-trades-heading" style={{ fontFamily: FONT_HEAD, fontSize: '1.25rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '2px', fontWeight: 400 }}>
        {t('resources.payGuide.popularTitle')}
      </h2>
      <p style={{ fontSize: '0.875rem', color: HELPER, lineHeight: 1.5, marginBottom: '10px' }}>
        {t('resources.payGuide.popularHint')}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${isPhone ? 2 : 4}, minmax(0, 1fr))`, gap: isPhone ? '10px' : '12px' }}>
        {tiles.map(r => {
          const active = r.trade === selectedTrade;
          const median = fmtCurrency(r.median_pay);
          const union = unionTrades.has(r.trade);
          return (
            <button
              key={r.trade}
              type="button"
              data-popular-tile={r.trade}
              aria-pressed={active}
              onClick={() => onSelect(r.trade)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px',
                minHeight: '44px', minWidth: 0, width: '100%', textAlign: 'left',
                padding: isPhone ? '12px' : '14px', borderRadius: '12px', cursor: 'pointer', fontFamily: FONT_BODY,
                backgroundColor: active ? 'rgba(245,166,35,0.12)' : NAVY_MID,
                border: `2px solid ${active ? AMBER : 'rgba(255,255,255,0.1)'}`,
                margin: 0, boxSizing: 'border-box', outlineOffset: '2px',
              }}
            >
              <span style={{ color: AMBER, display: 'flex' }}><TradeIcon trade={r.trade} /></span>
              <span style={{ fontSize: isPhone ? '0.95rem' : '1rem', fontWeight: 700, color: active ? AMBER : WHITE, lineHeight: 1.25, overflowWrap: 'break-word', hyphens: 'manual' }}>
                {r.trade}
              </span>
              {median && (
                <span style={{ fontSize: '0.875rem', color: HELPER, lineHeight: 1.3 }}>
                  {t('resources.payGuide.popularMedian', { pay: median })}
                </span>
              )}
              {union && (
                <span data-union-badge style={{
                  marginTop: 'auto', alignSelf: 'flex-start', fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.25, color: AMBER,
                  backgroundColor: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.35)',
                  borderRadius: '6px', padding: '3px 8px',
                }}>
                  {t('resources.payGuide.popularUnionBadge')}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
